<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex align-items-center gap-2 mb-3">
          <h6 class="ts-title mb-0">Report dates</h6>
        </div>

        <div class="row g-3">
          <!-- Intro / helper text on the left -->
          <aside class="col-lg-4">
            <div class="card ts-card">
              <div class="ts-card-body">
                <p class="fw-bold mb-2">About these dates</p>
                <p class="text-xs text-muted mb-2">
                  These dates appear on printed report cards for the selected class:
                </p>
                <ul class="text-xs text-muted" style="padding-left: 1rem; line-height: 1.7;">
                  <li><strong>Opening</strong> — next term's start date</li>
                  <li><strong>Mid-term</strong> — mid-term break date</li>
                  <li><strong>Clinic</strong> — parent-teacher meeting</li>
                  <li><strong>Closing</strong> — end-of-term date</li>
                </ul>
                <p class="text-xs text-muted mb-0 mt-2">
                  Each class can have its own dates. Leave any field blank if not applicable.
                </p>
              </div>
            </div>
          </aside>

          <!-- Form on the right -->
          <section class="col-lg-8">
            <div class="card ts-card">
              <div class="ts-card-body">

                <div class="row g-3 mb-2">
                  <div class="col-12">
                    <label class="tw-label">Class <span class="tw-req">*</span></label>
                    <select v-model="selectedClassId" @change="onClassChange" class="tw-input">
                      <option :value="0" disabled>— Select a class —</option>
                      <option v-for="c in classOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </div>
                </div>

                <hr class="my-3" v-if="selectedClassId" />

                <!-- Skeleton when loading an existing class's dates -->
                <div v-if="selectedClassId && isLoading" class="row g-3">
                  <div v-for="n in 4" :key="'sk'+n" class="col-md-6">
                    <div class="ts-skel" style="width:90px;height:10px;margin-bottom:6px"></div>
                    <div class="ts-skel" style="height:38px"></div>
                  </div>
                </div>

                <!-- Date fields — shown only once a class is picked -->
                <div v-else-if="selectedClassId" class="row g-3">
                  <div class="col-md-6">
                    <label class="tw-label">Opening date</label>
                    <input v-model="form.openingDate" type="date" class="tw-input" />
                  </div>
                  <div class="col-md-6">
                    <label class="tw-label">Mid-term date</label>
                    <input v-model="form.midtermDate" type="date" class="tw-input" />
                  </div>
                  <div class="col-md-6">
                    <label class="tw-label">Clinic date</label>
                    <input v-model="form.clinicDate" type="date" class="tw-input" />
                  </div>
                  <div class="col-md-6">
                    <label class="tw-label">Closing date</label>
                    <input v-model="form.closingDate" type="date" class="tw-input" />
                  </div>

                  <div class="col-12 d-flex justify-content-end mt-2">
                    <button class="tw-btn tw-btn-primary" @click="saveDates" :disabled="isSaving">
                      {{ isSaving ? 'Saving...' : 'Save dates' }}
                    </button>
                  </div>
                </div>

                <!-- Prompt shown when no class picked yet -->
                <div v-else class="ts-empty">
                  <svg width="48" height="48" fill="none" stroke="currentColor" stroke-width="1" viewBox="0 0 24 24" style="opacity:.2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <p class="ts-empty-title">Pick a class to see its dates</p>
                </div>

              </div>
            </div>
          </section>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';

export default {
  name: "ReportDatesPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      classOptions: [],
      selectedClassId: 0,

      isLoading: false,
      isSaving: false,

      // Dates as strings in YYYY-MM-DD format (what <input type="date">
      // emits). Backend accepts ISO date strings and Jackson parses them
      // into LocalDate. Null for unset fields.
      form: this.blankForm(),
    };
  },

  mounted() {
    this.loadClasses();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  methods: {
    /**
     * Classes from the legacy settings endpoint. If/when we modernise
     * the classes dropdown endpoint, this one line changes.
     */
    async loadClasses() {
      try {
        const res = await apiService.getDataDynamic('/api/settings/loadclasses');
        const rows = Array.isArray(res) ? res : [];
        this.classOptions = rows
          .filter(r => r && (r.no || r.id))
          .map(r => ({ id: r.no || r.id, name: r.Class || r.class || '' }));
      } catch (e) { /* ignore */ }
    },

    async onClassChange() {
      this.form = this.blankForm();
      if (!this.selectedClassId) return;

      this.isLoading = true;
      try {
        const res = await apiService.apiClient.get(
          `/api/academics/report-dates/${this.selectedClassId}`
        );
        const d = res.data || {};
        // Backend returns LocalDate as ISO strings — no parsing needed.
        // Falsy values (null, undefined) become empty-string for <input type="date">.
        this.form.openingDate = d.openingDate || '';
        this.form.midtermDate = d.midtermDate || '';
        this.form.clinicDate = d.clinicDate || '';
        this.form.closingDate = d.closingDate || '';
      } catch (e) {
        swal("Error", this.readErr(e, "Could not load dates"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    async saveDates() {
      if (!this.selectedClassId) {
        swal("Required", "Pick a class first", "warning");
        return;
      }
      this.isSaving = true;
      try {
        const payload = {
          classField: this.selectedClassId,
          // Empty strings → null on the wire. The backend's
          // LocalDate fields allow null.
          openingDate: this.form.openingDate || null,
          midtermDate: this.form.midtermDate || null,
          clinicDate: this.form.clinicDate || null,
          closingDate: this.form.closingDate || null,
        };
        await apiService.apiClient.post('/api/academics/report-dates', payload);
        swal("Saved", "Dates saved for this class", "success");
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save"), "error");
      } finally {
        this.isSaving = false;
      }
    },

    blankForm() {
      return {
        openingDate: '',
        midtermDate: '',
        clinicDate: '',
        closingDate: '',
      };
    },

    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>
